<script setup lang="ts">
/**
 * ImageLightbox - A full-screen media viewer with zoom, pan, and download.
 *
 * Supports both images and videos:
 * - Images: Zoom in/out (buttons + mouse wheel), drag-to-pan, reset view
 * - Videos: Native HTML5 video player with controls
 * - Optional download link for both types
 *
 * @example Image (default)
 * ```vue
 * <ImageLightbox
 *   v-model:open="lightboxOpen"
 *   :src="imageUrl"
 *   :alt="fileName"
 *   :download-href="downloadUrl"
 * />
 * ```
 *
 * @example Video
 * ```vue
 * <ImageLightbox
 *   v-model:open="lightboxOpen"
 *   :src="videoUrl"
 *   :alt="fileName"
 *   media-type="video"
 *   :download-href="downloadUrl"
 * />
 * ```
 */

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog/index'
import { Download, RotateCcw, ZoomIn, ZoomOut } from '@lucide/vue'
import { nextTick, ref, watch } from 'vue'


const props = withDefaults(
  defineProps<{
    /** Controlled open state (use v-model:open) */
    open?: boolean
    /** Media source URL */
    src: string
    /** Alt text (images) or accessible label (videos) */
    alt: string
    /** Media type: 'image' (default) or 'video' */
    mediaType?: 'image' | 'video'
    /** Optional title for the dialog (accessibility) */
    title?: string
    /** Optional description shown in the toolbar */
    description?: string
    /** Optional download URL - when provided, shows download button */
    downloadHref?: string
    /** Download button label */
    downloadLabel?: string
  }>(),
  {
    open: false,
    mediaType: 'image',
    title: '',
    description: '',
    downloadHref: undefined,
    downloadLabel: 'Télécharger',
  },
)

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
}>()

// ============================================================================
// Image zoom/pan state
// ============================================================================

const ZOOM_MIN = 0.25
const ZOOM_MAX = 5
const ZOOM_STEP = 0.25

const zoom = ref(1)
const imageSize = ref<{ width: number; height: number } | null>(null)
const viewportRef = ref<HTMLElement | null>(null)
const panning = ref(false)
const panStart = ref<{ x: number; y: number; scrollLeft: number; scrollTop: number } | null>(null)

function computeFitZoom(): number {
  const el = viewportRef.value
  const size = imageSize.value
  if (!el || !size) return 1
  const padding = 32 // p-4 = 16px each side (width + height)
  const viewW = Math.max(1, el.clientWidth - padding)
  const viewH = Math.max(1, el.clientHeight - padding)
  const scaleX = viewW / size.width
  const scaleY = viewH / size.height
  const fitZoom = Math.min(scaleX, scaleY, 1) // Cap at 1 so we never scale up on open
  return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, fitZoom))
}

function onImageLoad(event: Event): void {
  const img = event.target as HTMLImageElement
  if (img?.naturalWidth && img?.naturalHeight) {
    imageSize.value = { width: img.naturalWidth, height: img.naturalHeight }
    zoom.value = computeFitZoom()
    void nextTick(() => {
      requestAnimationFrame(() => centerView())
    })
  }
}

function zoomTowardCenter(direction: 1 | -1): void {
  const el = viewportRef.value
  const size = imageSize.value
  if (!el || !size) {
    if (direction > 0) zoom.value = Math.min(ZOOM_MAX, zoom.value + ZOOM_STEP)
    else zoom.value = Math.max(ZOOM_MIN, zoom.value - ZOOM_STEP)
    return
  }
  const oldZoom = zoom.value
  const newZoom =
    direction > 0
      ? Math.min(ZOOM_MAX, oldZoom + ZOOM_STEP)
      : Math.max(ZOOM_MIN, oldZoom - ZOOM_STEP)
  if (newZoom === oldZoom) return

  const viewW = el.clientWidth
  const viewH = el.clientHeight
  const centerX = el.scrollLeft + viewW / 2
  const centerY = el.scrollTop + viewH / 2
  const imageX = centerX / oldZoom
  const imageY = centerY / oldZoom

  zoom.value = newZoom
  void nextTick(() => {
    const el2 = viewportRef.value
    if (!el2) return
    const contentW = size.width * newZoom
    const contentH = size.height * newZoom
    const scrollLeft = Math.max(
      0,
      Math.min(imageX * newZoom - viewW / 2, contentW - viewW)
    )
    const scrollTop = Math.max(
      0,
      Math.min(imageY * newZoom - viewH / 2, contentH - viewH)
    )
    el2.scrollLeft = scrollLeft
    el2.scrollTop = scrollTop
  })
}

function zoomIn(): void {
  zoomTowardCenter(1)
}

function zoomOut(): void {
  zoomTowardCenter(-1)
}

function resetView(): void {
  zoom.value = computeFitZoom()
  void nextTick(() => {
    requestAnimationFrame(() => centerView())
  })
}

function centerView(): void {
  const el = viewportRef.value
  if (!el) return
  const contentW = el.scrollWidth
  const contentH = el.scrollHeight
  const viewW = el.clientWidth
  const viewH = el.clientHeight
  el.scrollLeft = Math.max(0, (contentW - viewW) / 2)
  el.scrollTop = Math.max(0, (contentH - viewH) / 2)
}

function onWheel(event: WheelEvent): void {
  event.preventDefault()
  if (event.deltaY < 0) {
    zoomIn()
  } else {
    zoomOut()
  }
}

function onPanStart(event: MouseEvent): void {
  const el = viewportRef.value
  if (!el || (event.target as HTMLElement).closest('a, button')) return
  panning.value = true
  panStart.value = {
    x: event.clientX,
    y: event.clientY,
    scrollLeft: el.scrollLeft,
    scrollTop: el.scrollTop,
  }
  document.addEventListener('mousemove', onPanMove)
  document.addEventListener('mouseup', onPanEnd)
}

function onPanMove(event: MouseEvent): void {
  if (!panning.value || !panStart.value) return
  const el = viewportRef.value
  if (!el) return
  const dx = event.clientX - panStart.value.x
  const dy = event.clientY - panStart.value.y
  el.scrollLeft = panStart.value.scrollLeft - dx
  el.scrollTop = panStart.value.scrollTop - dy
}

function onPanEnd(): void {
  panning.value = false
  panStart.value = null
  document.removeEventListener('mousemove', onPanMove)
  document.removeEventListener('mouseup', onPanEnd)
}

function resetState(): void {
  zoom.value = 1
  imageSize.value = null
  panning.value = false
  panStart.value = null
  document.removeEventListener('mousemove', onPanMove)
  document.removeEventListener('mouseup', onPanEnd)
}

function onOpenChange(value: boolean): void {
  if (!value) resetState()
  emit('update:open', value)
}

watch(() => props.open, (isOpen) => {
  if (!isOpen) resetState()
})

function onPointerDownOutside(): void {
  onOpenChange(false)
}

function onEscapeKeyDown(): void {
  onOpenChange(false)
}
</script>

<template>
  <Dialog :open="open" @update:open="onOpenChange">
    <DialogContent
      class="fixed inset-0 z-50 flex h-[100dvh] w-[100dvw] max-h-none max-w-none min-h-screen min-w-screen translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 bg-transparent p-0 shadow-none sm:max-w-none [&_[data-slot=dialog-close]]:right-4 [&_[data-slot=dialog-close]]:top-4 [&_[data-slot=dialog-close]]:rounded-full [&_[data-slot=dialog-close]]:bg-white/95 [&_[data-slot=dialog-close]]:p-2.5 [&_[data-slot=dialog-close]]:text-neutral-900 [&_[data-slot=dialog-close]]:opacity-100 [&_[data-slot=dialog-close]]:shadow-lg [&_[data-slot=dialog-close]]:transition-colors [&_[data-slot=dialog-close]]:hover:bg-white [&_[data-slot=dialog-close]]:hover:opacity-100 [&_[data-slot=dialog-close]_svg]:size-5 [&_[data-slot=dialog-close]_svg]:shrink-0"
      @pointer-down-outside="onPointerDownOutside"
      @escape-key-down="onEscapeKeyDown"
    >
      <DialogHeader class="sr-only">
        <DialogTitle>{{ title || alt }}</DialogTitle>
      </DialogHeader>
      <div class="flex min-h-0 flex-1 flex-col">
        <!-- VIDEO MODE -->
        <template v-if="mediaType === 'video'">
          <div class="flex min-h-0 flex-1 items-center justify-center p-4">
            <video
              :src="src"
              controls
              autoplay
              class="max-h-full max-w-full rounded-md"
              :aria-label="alt"
            >
              {{ alt }}
            </video>
          </div>
        </template>

        <!-- IMAGE MODE (default) -->
        <template v-else>
          <div
            ref="viewportRef"
            class="min-h-0 flex-1 select-none cursor-grab overflow-auto p-4 active:cursor-grabbing"
            :class="{ 'cursor-grabbing': panning }"
            @wheel.prevent="onWheel"
            @mousedown="onPanStart"
          >
            <div class="flex min-h-full min-w-full">
              <div
                class="relative m-auto shrink-0"
                :style="imageSize ? { width: `${imageSize.width * zoom}px`, height: `${imageSize.height * zoom}px` } : undefined"
              >
              <img
                :src="src"
                :alt="alt"
                class="pointer-events-none rounded-md"
                draggable="false"
                :class="imageSize ? 'max-w-none max-h-none' : 'max-h-[70vh] max-w-full object-contain'"
                :style="imageSize ? {
                  width: `${imageSize.width}px`,
                  height: `${imageSize.height}px`,
                  transform: `scale(${zoom})`,
                  transformOrigin: '0 0',
                } : undefined"
                @load="onImageLoad"
              />
            </div>
          </div>
        </div>
        </template>

      <!-- Toolbar -->
      <div class="flex shrink-0 items-center justify-between gap-2 border-t border-white/20 px-3 py-2">
        <!-- Image zoom controls (hidden for video) -->
        <div v-if="mediaType !== 'video'" class="flex items-center gap-1">
          <button
            type="button"
            class="rounded p-1.5 text-white/80 hover:bg-white/20 hover:text-white disabled:opacity-40"
            :disabled="zoom <= ZOOM_MIN"
            title="Zoom out"
            @click="zoomOut"
          >
            <ZoomOut class="h-4 w-4" />
          </button>
          <span class="min-w-[3rem] text-center text-sm text-white/80">
            {{ Math.round(zoom * 100) }}%
          </span>
          <button
            type="button"
            class="rounded p-1.5 text-white/80 hover:bg-white/20 hover:text-white disabled:opacity-40"
            :disabled="zoom >= ZOOM_MAX"
            title="Zoom in"
            @click="zoomIn"
          >
            <ZoomIn class="h-4 w-4" />
          </button>
          <button
            type="button"
            class="rounded p-1.5 text-white/80 hover:bg-white/20 hover:text-white"
            title="Reset view"
            @click="resetView"
          >
            <RotateCcw class="h-4 w-4" />
          </button>
        </div>
        <!-- Spacer when no zoom controls -->
        <div v-else />

        <div v-if="title || description" class="min-w-0 flex-1 truncate text-center">
          <span v-if="title" class="text-sm font-medium text-white">{{ title }}</span>
          <span v-if="title && description" class="text-white/40"> &mdash; </span>
          <span v-if="description" class="text-sm text-white/70">{{ description }}</span>
        </div>

        <a
          v-if="downloadHref"
          :href="downloadHref"
          class="inline-flex items-center gap-1 rounded-md bg-white/20 px-2 py-1.5 text-sm text-white hover:bg-white/30"
        >
          <Download class="h-4 w-4" />
          {{ downloadLabel }}
        </a>
      </div>
    </div>
  </DialogContent>
</Dialog>
</template>
